import type {ContributedInstructionKind, ContributedInstructionsMetadata} from '@github-ui/ahp-session-client/contracts'
import {
  filterContributedInstructions,
  type InstructionScope,
} from '@github-ui/ahp-session-client/contributed-instructions'
import type {CopilotCustomInstructions} from '@github-ui/copilot-chat-types'
import type {
  ContributedInstruction,
  ContributedInstructionSnapshot,
} from '@github-ui/copilot-shared-ui/contexts/ContributedInstructionsContext'

const OWNER_STEM_MAX_LENGTH = 80
const OWNER_LABEL_MAX_LENGTH = 120

export interface RuleFile {
  name: string
  content: string
}

export interface InstructionRuleInput {
  personalInstructions: string | null
  customInstructions: CopilotCustomInstructions[]
  instructionScope?: InstructionScope
}

export function toContributedInstructionsMetadata(
  instructions: ContributedInstructionSnapshot,
  instructionScope: InstructionScope = 'repository',
): ContributedInstructionsMetadata {
  return filterContributedInstructions(instructions, instructionScope).map(({kind, label, ownerUrl}) => ({
    kind,
    label,
    ...(ownerUrl ? {ownerUrl} : {}),
  }))
}

export function mergeContributedInstructionsMetadata(
  ...snapshots: readonly ContributedInstructionsMetadata[]
): ContributedInstructionsMetadata {
  const merged = new Map<string, ContributedInstructionsMetadata[number]>()
  for (const snapshot of snapshots) {
    for (const {kind, label, ownerUrl} of snapshot) {
      const owner = kind === 'personal' ? '' : contributedInstructionOwner(label, ownerUrl)
      const key = `${kind}:${owner}`
      if (merged.has(key)) continue
      merged.set(key, {kind, ...(label ? {label} : {}), ...(ownerUrl ? {ownerUrl} : {})})
    }
  }
  return [...merged.values()]
}

const typeOrder: Record<'Organization' | 'Repository', number> = {
  Organization: 0,
  Repository: 1,
}

function sanitizeOwner(owner: string): string {
  return (
    owner
      .trim()
      .toLowerCase()
      .replace(/[^a-zA-Z0-9._-]/g, '-')
      .slice(0, OWNER_STEM_MAX_LENGTH) || 'unknown'
  )
}

function normalizeOwner(owner: string): string {
  return owner.trim().toLowerCase() || 'unknown'
}

function contributedInstructionOwner(label: string | undefined, ownerUrl: string | undefined): string {
  if (ownerUrl) {
    try {
      // eslint-disable-next-line no-restricted-syntax
      const url = new URL(ownerUrl)
      const owner = url.pathname.replace(/^\/+|\/+$/g, '')
      if ((url.protocol === 'https:' || url.protocol === 'http:') && owner) return normalizeOwner(owner)
    } catch {
      return normalizeOwner(label ?? '')
    }
  }
  return normalizeOwner(label ?? '')
}

function instructionOwnerUrl(kind: Extract<ContributedInstructionKind, 'organization' | 'repository'>, owner: string) {
  const expectedSegments = kind === 'organization' ? 1 : 2
  const segments = owner.split('/')
  if (segments.length !== expectedSegments || segments.some(segment => !/^[a-zA-Z0-9._-]+$/.test(segment))) {
    return undefined
  }
  return `https://github.com/${owner}`
}

function stableOwnerHash(owner: string): string {
  let firstHash = 0x811c9dc5
  let secondHash = 0x9e3779b9
  for (let index = 0; index < owner.length; index++) {
    const code = owner.charCodeAt(index)
    firstHash = Math.imul(firstHash ^ code, 0x01000193)
    secondHash = Math.imul(secondHash ^ code, 0x85ebca6b)
  }
  return [firstHash, secondHash].map(hash => (hash >>> 0).toString(16).padStart(8, '0')).join('')
}

function ownerLabel(owner: string): string {
  return owner.trim().replace(/\s+/g, ' ').slice(0, OWNER_LABEL_MAX_LENGTH) || 'unknown'
}

function buildRule(description: string, prompt: string, scope?: string): string {
  const body = scope ? `${scope}\n\n${prompt}` : prompt
  return `---
description: ${description}
alwaysApply: true
---

${body}`
}

export function deriveInstructionRules({
  personalInstructions,
  customInstructions,
  instructionScope,
}: InstructionRuleInput): RuleFile[] {
  const rules = new Map<string, RuleFile>()

  for (const instruction of normalizeContributedInstructions({
    personalInstructions,
    customInstructions,
    instructionScope,
  })) {
    if (instruction.kind === 'personal') {
      rules.set('personal.mdc', {
        name: 'personal.mdc',
        content: buildRule('Personal custom instructions', instruction.prompt),
      })
      continue
    }

    const prefix = instruction.kind === 'organization' ? 'org' : 'repo'
    const normalizedOwner = normalizeOwner(instruction.label)
    const name = `${prefix}-${sanitizeOwner(instruction.label)}-${stableOwnerHash(normalizedOwner)}.mdc`
    const scopeNoun = instruction.kind
    rules.set(name, {
      name,
      content: buildRule(
        `Custom instructions for ${scopeNoun} ${instruction.label}`,
        instruction.prompt,
        `These instructions apply to the ${scopeNoun} ${instruction.label}.`,
      ),
    })
  }

  return [...rules.values()]
}

export function normalizeContributedInstructions({
  personalInstructions,
  customInstructions,
  instructionScope = 'repository',
}: InstructionRuleInput): ContributedInstructionSnapshot {
  const instructions: ContributedInstruction[] = []
  if (personalInstructions?.trim()) {
    instructions.push({
      kind: 'personal',
      label: 'Personal instructions',
      prompt: personalInstructions,
    })
  }

  const sortedInstructions = customInstructions
    .filter(
      (customInstruction): customInstruction is CopilotCustomInstructions & {type: 'Organization' | 'Repository'} =>
        customInstruction.type !== 'Assistant' && !!customInstruction.prompt.trim(),
    )
    .toSorted((left, right) => {
      const typeDifference = typeOrder[left.type] - typeOrder[right.type]
      if (typeDifference !== 0) return typeDifference
      const stemDifference = sanitizeOwner(left.owner).localeCompare(sanitizeOwner(right.owner))
      if (stemDifference !== 0) return stemDifference
      return normalizeOwner(left.owner).localeCompare(normalizeOwner(right.owner))
    })

  const seenOwners = new Set<string>()
  for (const customInstruction of sortedInstructions) {
    const normalizedOwner = normalizeOwner(customInstruction.owner)
    const kind = customInstruction.type === 'Organization' ? 'organization' : 'repository'
    const ownerKey = `${kind}:${normalizedOwner}`
    if (seenOwners.has(ownerKey)) continue
    seenOwners.add(ownerKey)
    const label = ownerLabel(customInstruction.owner)
    const ownerUrl = instructionOwnerUrl(kind, label)
    instructions.push({
      kind,
      label,
      prompt: customInstruction.prompt,
      ...(ownerUrl ? {ownerUrl} : {}),
    })
  }

  return filterContributedInstructions(instructions, instructionScope)
}
